import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/layout/NavBar';
import SignUpPage from './pages/Signup';
import LeadsDashboard from './pages/LeadsDashboard';
import LandingPage from './pages/LandingPage';
import SignInPage from './pages/Signin';
import AddOrganizationPage from './pages/AddOrganizationPage';
import LeadDetailsPage from './pages/LeadDetailsPage';
import { EditLeadDetailsPage } from './pages/EditLeadDetailsPage';
import InviteEmployeePage from './pages/InviteEmployeePage';
import TrialAndSubscriptionPage from './pages/TrialAndSubscription';
import AcceptInvitePage from './pages/AcceptInvitePage';
import { Analytics } from '@vercel/analytics/react';
import TasksDashboard from './pages/tasks/TasksDashboard';
import AddTaskPage from './pages/tasks/AddTaskPage';
import EditTaskPage from './pages/tasks/EditTaskPage';
import ViewTaskPage from './pages/tasks/ViewTaskPage';
import SubscriptionPage from './pages/SubscriptionPage';
import OrganizationCreateProcessPage from './pages/OrganizationCreateProcessPage';
import { ReactElement, useEffect } from 'react';
import ScrollToTop from './utils/ScrollToTop';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyAgreement from './pages/PrivacyAgreement';
import TeamDashboard from './pages/team/TeamDashboard';
import VersionChecker from './components/VersionChecker';
import CataloguePage from './pages/catalogue/CataloguePage';
import AddCatalogueItemPage from './pages/catalogue/AddCatalogueItemPage';
import CatalogueItemDetailsPage from './pages/catalogue/CatalogueItemDetailsPage';
import CompanyManagement from './pages/CompanyManagement';
import CompanyEditorPage from './pages/CompanyEditorPage';
import CompanyDetailsPage from './pages/CompanyDetailsPage';
import CustomersDashboard from './pages/CustomersDashboard';
import OrganizationManagementPage from './pages/OrganizationManagementPage';
import QuotesPage from './pages/QuotesPage';
import AddNewCustomerPage from './pages/AddNewCustomerPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import CheckOutPage from './pages/CheckOut';
import Layout from './pages/whatsapp/components/Layout';
import Templates from './pages/whatsapp/pages/TemplatesPage';
import { WhatsAppOnboarding } from './pages/whatsapp/pages/WhatsAppOnboarding';
import PasteTextAI from './pages/catalogue/AddCatalogueItemByTextAIPage';
import UploadPictureAI from './pages/catalogue/AddCatalogueItemByImageAIPage';
import RecordVoiceAddCatalogueItem from './pages/catalogue/AddCatalogueItemByVoicePage';
import ViewQuote from './pages/quotes/pages/ViewQuote';
import EditQuote from './pages/EditQuote';
import ConversationDetailSmall from './pages/whatsapp/pages/ConversationDetailSmall';
import { InstagramOnboarding } from './pages/instagram/pages/InstagramOnboarding';
import InstagramConversationDetailSmall from './pages/instagram/pages/InstagramConversationDetailSmall';
import InstagramLayout from './pages/instagram/components/InstagramLayout';
import { FacebookOnboarding } from './pages/facebook_messenger/pages/FacebookOnboarding';
import FacebookConversationDetailSmall from './pages/facebook_messenger/pages/FacebookConversationDetailSmall';
import FacebookMessengerLayout from './pages/facebook_messenger/components/FacebookLayout';
import { CalendarPage } from './pages/calendar/CalendarPage';
import HomePrimaryDashboard from './pages/HomePrimaryDashboard';
import InstagramDirectLogInSuccessPage from './pages/instagram/pages/InstagramDirectLogInSuccessPage';
import ViewCustomerPage from './pages/ViewCustomerPage';
import EditCustomerPage from './pages/EditCustomerPage';
import NewQuotePage from './pages/quotes/pages/NewQuotePage';
import ViewEventPage from './pages/calendar/ViewEvent';
import EditEventPage from './pages/calendar/EditEvent';
import EditCatalogueItemPage from './pages/catalogue/EditCatalogueItem';
import EditOrganizationPage from './pages/OrganizationDataEditPage';
import ManageSubscriptionPage from './pages/ManageSubscriptionPage';
import CreateEventPage from './pages/calendar/CreateEvent';
import OrganizationAIDataPage from './pages/organization_ai_data/OrganizationAIDataPage';
import ViewEmployeePage from './pages/ViewEmployeePage';
import EditTeamMemberProfile from './pages/team/EditTeamMember';
import OrdersListPage from './pages/orders/OrdersPage';
import ViewOrderPage from './pages/orders/ViewOrderPage';
import EditOrderPage from './pages/orders/EditOrderPage';
import CreateOrderPage from './pages/orders/CreateOrderPage';
import WhatsAppSettingsPage from './pages/whatsapp/pages/Settings';
import TeamChatComingSoon from './pages/teamchat/TeamChatPage';
import WhatsAppSendPage from './pages/whatsapp/pages/TestSendMsgPage';
import { TeamChatsPage } from './pages/teamchat/TeamChats';
import { TeamChatConversationPage } from './pages/teamchat/TeamConversation';
import SelectTeamMemberToChatPage from './pages/teamchat/SelectTeamMemberToChatPage';
import EmailComingSoonPage from './pages/email/EmailComingSoonPage';
import { messaging } from './hooks/firebase-config';
import CatalogueItemImages from './pages/catalogue/CatalogueItemImages';
import InstagramSettings from './pages/instagram/pages/InstagramSettings';
import FacebookLogInSuccessPage from './pages/facebook_messenger/pages/FacebookOnboardingSuccess';
import FacebookPageSettings from './pages/facebook_messenger/pages/FacebookPageSettings';
import InstagramCommentsPage from './pages/instagram/pages/InstagramCommentsPage';
import FacebookCommentsPage from './pages/facebook_messenger/pages/FacebookCommentsPage';
import AddNewLeadPage from './pages/AddNewLeadPage';
import AccountInformationPage from './pages/my_account/AccountInformationPage';
import Chatbot from './pages/chatbot_test/chatbot';
import WebsiteComingSoonPage from './pages/website/WebsiteComingSoonPage';
import InboxPage from './pages/InboxPage';
import EditBusinessNamePage from './pages/whatsapp/pages/EditBusinessNamePage';
import EditAboutPage from './pages/whatsapp/pages/EditAboutPage';
import PhoneNumberPage from './pages/whatsapp/pages/PhoneNumberPage';
import EditEmailPage from './pages/whatsapp/pages/EditEmailPage';
import EditWebsitePage from './pages/whatsapp/pages/EditWebsitePage';
import EditAddressPage from './pages/whatsapp/pages/EditAddressPage';
import EditProfilePhotoPage from './pages/whatsapp/pages/EditProfilePhotoPage';
import FacebookUtilityMessagesListPage from './pages/facebook_messenger/pages/FacebookUtilityMessagesListPage';
import FacebookNewUtilityMessagePage from './pages/facebook_messenger/pages/FacebookNewUtilityMessagePage';
import RequireAuth from './components/routing/RequireAuth';
import RequireGuest from './components/routing/RequireGuest';
import { useAuthSession } from './hooks/useAuthSession';
import { listenForNotifications } from './hooks/notifications';

function App() {
  const { isAuthenticated, loading: authLoading } = useAuthSession();

  const protect = (element: ReactElement) => (
    <RequireAuth isAuthenticated={isAuthenticated} loading={authLoading}>
      {element}
    </RequireAuth>
  );

  const guestOnly = (element: ReactElement) => (
    <RequireGuest isAuthenticated={isAuthenticated} loading={authLoading}>
      {element}
    </RequireGuest>
  );

  useEffect(() => {
    messaging();
    listenForNotifications();
  }, []);

  return (
    <Router>
      <VersionChecker />
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={protect(<HomePrimaryDashboard />)} />
        <Route
          path="organization/:orgId/info"
          element={protect(<OrganizationAIDataPage />)}
        />
        <Route
          path="organization/:orgId/info/edit"
          element={protect(<EditOrganizationPage />)}
        />
        <Route path="/pricing" element={<TrialAndSubscriptionPage />} />
        <Route path="/refundpolicy" element={<RefundPolicyPage />} />
        <Route path="/termsandconditions" element={<TermsAndConditions />} />
        <Route path="/privacyagreement" element={<PrivacyAgreement />} />
        <Route path="/subscription" element={protect(<SubscriptionPage />)} />
        <Route
          path="/organization/:id"
          element={protect(<HomePrimaryDashboard />)}
        />
        <Route
          path="/organization/create"
          element={protect(<OrganizationCreateProcessPage />)}
        />
        <Route
          path="/organization/:orgId/subscription/"
          element={protect(<ManageSubscriptionPage />)}
        />

        {/* quotes */}
        <Route
          path="/organization/:orgId/quotes/"
          element={protect(<QuotesPage />)}
        />
        <Route
          path="/organization/:orgId/quotes/new"
          element={protect(<NewQuotePage />)}
        />
        <Route
          path="/organization/:orgId/quotes/view/:quoteId"
          element={protect(<ViewQuote />)}
        />
        <Route
          path="/organization/:orgId/quotes/:quoteId/edit"
          element={protect(<EditQuote />)}
        />

        {/* tasks */}
        <Route
          path="/organization/:orgId/tasks/"
          element={protect(<TasksDashboard />)}
        />
        <Route
          path="/organization/:orgId/tasks/:taskId/edit"
          element={protect(<EditTaskPage />)}
        />

        {/* email */}
        <Route
          path="/organization/:orgId/inbox/email"
          element={protect(<EmailComingSoonPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/smart"
          element={protect(<InboxPage />)}
        />

        <Route
          path="/organization/:orgId/manage-companies/"
          element={protect(<CompanyManagement />)}
        />
        <Route
          path="/organization/:orgId/manage-companies/new"
          element={protect(<CompanyEditorPage />)}
        />
        <Route
          path="/organization/:orgId/manage-companies/:companyId"
          element={protect(<CompanyDetailsPage />)}
        />
        <Route
          path="/organization/:orgId/manage-companies/:companyId/edit"
          element={protect(<CompanyEditorPage />)}
        />
        <Route
          path="/organization/:orgId/catalogue/"
          element={protect(<CataloguePage />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/new/form"
          element={protect(<AddCatalogueItemPage />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/new/text-ai"
          element={protect(<PasteTextAI />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/new/image-ai"
          element={protect(<UploadPictureAI />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/new/voice"
          element={protect(<RecordVoiceAddCatalogueItem />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/:itemId"
          element={protect(<CatalogueItemDetailsPage />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/:itemId/images"
          element={protect(<CatalogueItemImages />)}
        />
        <Route
          path="/organization/:orgId/catalogue-item/:itemId/images"
          element={protect(<CatalogueItemImages />)}
        />
        <Route
          path="/organization/:orgId/catalogue/:catalogueId/catalogue-item/:itemId/edit"
          element={protect(<EditCatalogueItemPage />)}
        />
        <Route
          path="/organization/:orgId/tasks/:taskId/view"
          element={protect(<ViewTaskPage />)}
        />
        <Route
          path="/organization/:orgId/tasks/new"
          element={protect(<AddTaskPage />)}
        />
        <Route
          path="/organization/:orgId/leads/new"
          element={protect(<AddNewLeadPage />)}
        />
        <Route
          path="/organization/:orgId/customers/new"
          element={protect(<AddNewCustomerPage />)}
        />
        <Route
          path="/organization/:orgId/customers/:customerId"
          element={protect(<ViewCustomerPage />)}
        />
        <Route
          path="/organization/:orgId/customers/:customerId/edit"
          element={protect(<EditCustomerPage />)}
        />
        <Route
          path="/organization/:orgId/team/"
          element={protect(<TeamDashboard />)}
        />
        <Route
          path="/organization/:orgId/team/:teamMemberId"
          element={protect(<ViewEmployeePage />)}
        />
        <Route
          path="/organization/:orgId/team/:teamMemberId/edit"
          element={protect(<EditTeamMemberProfile />)}
        />
        <Route
          path="/organization/:orgId/settings/"
          element={protect(<OrganizationManagementPage />)}
        />
        <Route
          path="/organization/:orgId/employees/new"
          element={protect(<InviteEmployeePage />)}
        />
        <Route
          path="/organization/:orgId/invite/accept/:inviteCode"
          element={<AcceptInvitePage />}
        />
        <Route
          path="/organization/:orgId/leads/:leadId"
          element={protect(<LeadDetailsPage />)}
        />
        <Route
          path="/organization/:orgId/leads/:leadId/edit"
          element={protect(<EditLeadDetailsPage />)}
        />
        <Route
          path="/new-organization"
          element={protect(<AddOrganizationPage />)}
        />
        <Route path="/staging/checkout" element={<CheckOutPage />} />
        <Route path="/sign-up" element={guestOnly(<SignUpPage />)} />
        <Route path="/sign-in" element={guestOnly(<SignInPage />)} />
        <Route
          path="/organization/:orgId/leads-dashboard"
          element={protect(<LeadsDashboard />)}
        />
        {/* info */}
        <Route
          path="/organization/:orgId/customers-dashboard"
          element={protect(<CustomersDashboard />)}
        />

        {/* orders */}
        <Route
          path="/organization/:orgId/orders"
          element={protect(<OrdersListPage />)}
        />
        <Route
          path="/organization/:orgId/orders/new"
          element={protect(<CreateOrderPage />)}
        />
        <Route
          path="/organization/:orgId/orders/:orderId"
          element={protect(<ViewOrderPage />)}
        />
        <Route
          path="/organization/:orgId/orders/:orderId/edit"
          element={protect(<EditOrderPage />)}
        />

        {/* Calendar */}
        <Route
          path="/organization/:orgId/calendar"
          element={protect(<CalendarPage />)}
        />
        <Route
          path="/organization/:orgId/events/:eventId/view"
          element={protect(<ViewEventPage />)}
        />
        <Route
          path="/organization/:orgId/events/:eventId/edit"
          element={protect(<EditEventPage />)}
        />
        <Route
          path="/organization/:orgId/events/new"
          element={protect(<CreateEventPage />)}
        />

        <Route
          path="/organization/:orgId/chatbot"
          element={protect(<Chatbot />)}
        />

        {/* Team Chat */}
        <Route
          path="/organization/:orgId/team-chat"
          element={protect(<TeamChatComingSoon />)}
        />

        {/* User Details */}
        <Route
          path="/user/account/"
          element={protect(<AccountInformationPage />)}
        />

        {/* WhatsApp */}
        <Route
          path="/organization/:orgId/inbox/whatsapp/test"
          element={protect(<WhatsAppSendPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/settings"
          element={protect(<WhatsAppSettingsPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-business-name"
          element={protect(<EditBusinessNamePage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-about"
          element={protect(<EditAboutPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-phone-number"
          element={protect(<PhoneNumberPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-email"
          element={protect(<EditEmailPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-website"
          element={protect(<EditWebsitePage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-address"
          element={protect(<EditAddressPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/:whatsappNumberId/settings/edit-prophile-photo"
          element={protect(<EditProfilePhotoPage />)}
        />
        <Route
          path="/organization/whatsapp"
          element={protect(<WhatsAppOnboarding />)}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/conversations/:convId"
          element={protect(
            <Layout>
              <ConversationDetailSmall />
            </Layout>,
          )}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/conversations"
          element={protect(
            <Layout>
              <></>
            </Layout>,
          )}
        />
        <Route
          path="/organization/:orgId/inbox/whatsapp/templates"
          element={protect(
            <Layout>
              <Templates />
            </Layout>,
          )}
        />

        {/* Instagram */}
        <Route
          path="/organization/inbox/instagram"
          element={protect(<InstagramOnboarding />)}
        />
        <Route
          path="/organization/inbox/instagram/success"
          element={protect(<InstagramDirectLogInSuccessPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/instagram/:instagramAccId/comments"
          element={protect(<InstagramCommentsPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/instagram/conversations/:convId"
          element={protect(<InstagramConversationDetailSmall />)}
        />
        <Route
          path="/organization/:orgId/inbox/instagram/:profileId/settings"
          element={protect(<InstagramSettings />)}
        />
        <Route
          path="/organization/:orgId/inbox/instagram/conversations"
          element={protect(
            <InstagramLayout>
              {null}
            </InstagramLayout>,
          )}
        />
        <Route
          path="/organization/:orgId/inbox/instagram/templates"
          element={protect(
            <InstagramLayout>
              <Templates />
            </InstagramLayout>,
          )}
        />

        {/* Facebook */}
        <Route
          path="/organization/inbox/facebook-messenger"
          element={protect(<FacebookOnboarding />)}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/conversations/:convId"
          element={protect(
            <FacebookMessengerLayout>
              <FacebookConversationDetailSmall />
            </FacebookMessengerLayout>,
          )}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/:profileId/settings"
          element={protect(<FacebookPageSettings />)}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/:profileId/settings/utility-messages"
          element={protect(<FacebookUtilityMessagesListPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/:profileId/settings/utility-messages/new"
          element={protect(<FacebookNewUtilityMessagePage />)}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/:facebookAccId/comments"
          element={protect(<FacebookCommentsPage />)}
        />
        <Route
          path="/organization/:orgId/inbox/facebook/conversations"
          element={protect(
            <FacebookMessengerLayout>
              <></>
            </FacebookMessengerLayout>,
          )}
        />
        <Route
          path="/organization/:orgId/inbox/facebook-messenger/templates"
          element={protect(
            <FacebookMessengerLayout>
              <Templates />
            </FacebookMessengerLayout>,
          )}
        />

        <Route
          path="/organization/inbox/facebook/success"
          element={protect(<FacebookLogInSuccessPage />)}
        />

        {/* Team Chat */}
        <Route
          path="/organization/:orgId/teamchat/intro/"
          element={<Navigate to="../conversations/" replace />}
        />
        <Route
          path="/organization/:orgId/teamchat/conversations/"
          element={protect(<TeamChatsPage />)}
        />
        <Route
          path="/organization/:orgId/teamchat/select/"
          element={protect(<SelectTeamMemberToChatPage />)}
        />
        <Route
          path="/organization/:orgId/teamchat/conversations/:conversationId"
          element={protect(<TeamChatConversationPage />)}
        />
        <Route
          path="/organization/:orgId/website"
          element={protect(<WebsiteComingSoonPage />)}
        />
      </Routes>
      <Analytics />
    </Router>
  );
}

export default App;
